import { useEffect, useRef, useState } from "react";
import axios from "axios";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet-draw";
import "leaflet-draw/dist/leaflet.draw.css";
import {
  X, Sparkles, PenLine, Pencil, Undo2, Eraser, RotateCcw, Search, ShieldCheck,
  AlertTriangle, Check, Layers,
} from "lucide-react";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { US_STATES } from "@/lib/usStates";
import { STATES_BY_COUNTRY } from "@/lib/geoData";

const inputClass = "w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm dark:border-white/15 dark:bg-white/5 dark:text-white";
const labelClass = "text-xs font-bold uppercase tracking-[0.12em] text-slate-400 dark:text-slate-500";
const toolBtn = "inline-flex items-center gap-1.5 rounded-lg border px-3 py-2 text-xs font-bold transition-colors duration-150 disabled:opacity-40";
const toolBtnIdle = "border-slate-200 text-slate-600 hover:bg-slate-50 dark:border-white/15 dark:text-slate-200 dark:hover:bg-white/10";
const toolBtnActive = "border-[#0052FF] bg-[#0052FF] text-white";

export const ServiceAreaEditorModal = ({ area, availableServices, onClose, onSaved }) => {
  const { API, user } = useAuth();
  const orgCountry = user?.organization?.address?.country || "US";
  const isUS = orgCountry === "US";
  const stateOptions = isUS
    ? US_STATES
    : (STATES_BY_COUNTRY[orgCountry] || []).map((name) => ({ code: name, name }));
  const mapElRef = useRef(null);
  const mapRef = useRef(null);
  const drawnItemsRef = useRef(null);
  const circleRef = useRef(null);
  const editHandlerRef = useRef(null);
  const historyRef = useRef([]);
  const searchTimerRef = useRef(null);

  const [areaId, setAreaId] = useState(area?.service_area_id || null);
  const [name, setName] = useState(area?.name || "");
  const [description, setDescription] = useState(area?.description || "");
  const [status, setStatus] = useState(area?.status || "Active");
  const [allServices, setAllServices] = useState(!area || !(area.service_ids?.length));
  const [selectedServiceIds, setSelectedServiceIds] = useState(new Set(area?.service_ids || []));

  const [rightTab, setRightTab] = useState("ai");
  const [aiText, setAiText] = useState("");
  const [interpretation, setInterpretation] = useState(null);
  const [preview, setPreview] = useState(null);
  const [regions, setRegions] = useState([]);
  const [manualOverride, setManualOverride] = useState(false);
  const [loadedGeo, setLoadedGeo] = useState(null);
  const [editingPoints, setEditingPoints] = useState(false);

  const [selectedState, setSelectedState] = useState("");
  const [counties, setCounties] = useState([]);
  const [countyQuery, setCountyQuery] = useState("");
  const [selectedCounties, setSelectedCounties] = useState(new Set());
  const [loadingCounties, setLoadingCounties] = useState(false);

  const [places, setPlaces] = useState([]);
  const [placeQuery, setPlaceQuery] = useState("");
  const [selectedPlaces, setSelectedPlaces] = useState(new Set());
  const [loadingPlaces, setLoadingPlaces] = useState(false);

  const [zipInput, setZipInput] = useState("");
  const [validatingZip, setValidatingZip] = useState(false);
  const [zipError, setZipError] = useState("");

  const [overlaps, setOverlaps] = useState([]);
  const overlapTimerRef = useRef(null);

  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);

  const [interpreting, setInterpreting] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  useEffect(() => {
    if (!mapElRef.current || mapRef.current) return;
    const map = L.map(mapElRef.current, { attributionControl: false, zoomAnimation: false, fadeAnimation: false, markerZoomAnimation: false }).setView([39.5, -98.35], 4);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { maxZoom: 18 }).addTo(map);
    const drawnItems = new L.FeatureGroup().addTo(map);
    drawnItemsRef.current = drawnItems;
    map.on(L.Draw.Event.CREATED, (e) => {
      drawnItems.clearLayers();
      drawnItems.addLayer(e.layer);
      setManualOverride(true);
      checkOverlapDebounced();
    });
    mapRef.current = map;
    let cancelled = false;
    const t = setTimeout(() => { if (!cancelled) { try { map.invalidateSize(); } catch { /* leaflet unmount race */ } } }, 0);
    return () => { cancelled = true; clearTimeout(t); try { map.remove(); } catch { /* ignore */ } mapRef.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!areaId) return;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/org/service-areas/${areaId}/geography`, { withCredentials: true });
        if (data.geography) {
          setLoadedGeo(data.geography);
          if (data.geography.source_description) setAiText(data.geography.source_description);
          if (data.geography.boundary_entities?.length) setRegions(data.geography.boundary_entities.map((e) => ({ level: e.level, state: e.state, name: e.name })));
          setTimeout(() => renderGeometry(data.geography), 0);
        }
      } catch (err) {
        if (err?.response?.status !== 404) console.error("Failed to load service area geography", err);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [areaId]);

  const captureSnapshot = () => {
    if (circleRef.current) {
      const c = circleRef.current.getLatLng();
      return { kind: "radius", center: [c.lng, c.lat], radius_miles: circleRef.current.__radiusMiles || 10 };
    }
    if (drawnItemsRef.current) {
      const geoms = [];
      drawnItemsRef.current.eachLayer((layer) => geoms.push(layer.toGeoJSON().geometry));
      if (geoms.length) return { kind: "polygon", geometry: geoms.length === 1 ? geoms[0] : { type: "MultiPolygon", coordinates: geoms.map((g) => (g.type === "Polygon" ? g.coordinates : g.coordinates[0])) } };
    }
    return null;
  };

  const pushHistory = () => {
    historyRef.current.push(captureSnapshot());
    if (historyRef.current.length > 12) historyRef.current.shift();
  };

  const areaIdRef = useRef(areaId);
  useEffect(() => { areaIdRef.current = areaId; }, [areaId]);

  const checkOverlapDebounced = () => {
    clearTimeout(overlapTimerRef.current);
    overlapTimerRef.current = setTimeout(async () => {
      const snap = captureSnapshot();
      if (!snap) { setOverlaps([]); return; }
      try {
        const body = snap.kind === "radius"
          ? { center: snap.center, radius_miles: snap.radius_miles, exclude_service_area_id: areaIdRef.current }
          : { geometry: snap.geometry, exclude_service_area_id: areaIdRef.current };
        const { data } = await axios.post(`${API}/org/service-areas/check-overlap`, body, { withCredentials: true });
        setOverlaps(data.overlaps || []);
      } catch {
        /* non-blocking - ignore overlap-check failures */
      }
    }, 600);
  };

  const renderGeometry = (geo) => {
    const map = mapRef.current;
    if (!map || !drawnItemsRef.current) return;
    map.invalidateSize();
    drawnItemsRef.current.clearLayers();
    if (circleRef.current) { map.removeLayer(circleRef.current); circleRef.current = null; }
    if (geo?.kind === "radius") {
      const [lng, lat] = geo.center;
      circleRef.current = L.circle([lat, lng], { radius: (geo.radius_miles || 10) * 1609.34, color: "#0052FF", fillOpacity: 0.15 }).addTo(map);
      circleRef.current.__radiusMiles = geo.radius_miles || 10;
      setTimeout(() => {
        if (mapRef.current !== map || !circleRef.current) return;
        try { map.fitBounds(circleRef.current.getBounds(), { animate: false }); } catch (e) { /* leaflet unmount race */ }
      }, 0);
    } else if (geo?.geometry) {
      const gj = L.geoJSON(geo.geometry, { style: { color: "#0052FF", fillOpacity: 0.15 } });
      gj.eachLayer((layer) => drawnItemsRef.current.addLayer(layer));
      const bounds = gj.getBounds();
      if (bounds.isValid()) {
        setTimeout(() => {
          if (mapRef.current !== map) return;
          try { map.invalidateSize(); map.fitBounds(bounds, { animate: false }); } catch (e) { /* leaflet unmount race */ }
        }, 0);
      }
    }
    checkOverlapDebounced();
  };

  const runPreview = async (regionsList, flags = {}) => {
    if (!regionsList.length) {
      pushHistory();
      drawnItemsRef.current?.clearLayers();
      if (circleRef.current) { mapRef.current.removeLayer(circleRef.current); circleRef.current = null; }
      setPreview(null);
      return;
    }
    setPreviewing(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/org/service-areas/preview-geometry`, {
        llm_interpretation: { regions: regionsList, exclude_regions: flags.exclude_regions || [], is_informal_region: !!flags.is_informal_region },
      }, { withCredentials: true });
      pushHistory();
      setPreview(data);
      setManualOverride(false);
      renderGeometry(data.kind === "radius" ? { kind: "radius", center: data.center, radius_miles: data.radius_miles } : { kind: "polygon", geometry: data.geometry });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setPreviewing(false);
    }
  };

  const interpretAndPreview = async () => {
    if (!aiText.trim()) return;
    setInterpreting(true);
    setError("");
    setInterpretation(null);
    try {
      const { data } = await axios.post(`${API}/org/service-areas/interpret`, { description: aiText.trim() }, { withCredentials: true });
      setInterpretation(data.llm_interpretation);
      const nextRegions = data.llm_interpretation.regions || [];
      setRegions(nextRegions);
      await runPreview(nextRegions, data.llm_interpretation);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setInterpreting(false);
    }
  };

  const removeRegion = (idx) => {
    const next = regions.filter((_, i) => i !== idx);
    setRegions(next);
    runPreview(next, interpretation || {});
  };

  const onStateChange = (stateCode) => {
    setSelectedState(stateCode);
    setSelectedCounties(new Set());
    setSelectedPlaces(new Set());
    setCounties([]);
    setPlaces([]);
    setZipError("");
    if (!stateCode || !isUS) return;
    loadCounties(stateCode);
    loadPlaces(stateCode);
  };

  const loadCounties = async (stateCode) => {
    setLoadingCounties(true);
    try {
      const { data } = await axios.get(`${API}/org/service-areas/regions/counties`, { params: { state: stateCode }, withCredentials: true });
      setCounties(data.counties || []);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoadingCounties(false);
    }
  };

  const toggleCounty = (name) => {
    setSelectedCounties((prev) => {
      const next = new Set(prev);
      next.has(name) ? next.delete(name) : next.add(name);
      return next;
    });
  };

  const addWholeState = () => {
    if (!selectedState) return;
    const next = [...regions, { level: "state", name: selectedState }];
    setRegions(next);
    runPreview(next);
  };

  const addSelectedCounties = () => {
    if (!selectedCounties.size) return;
    const additions = Array.from(selectedCounties).map((name) => ({ level: "county", state: selectedState, name }));
    const next = [...regions, ...additions];
    setRegions(next);
    setSelectedCounties(new Set());
    runPreview(next);
  };

  const loadPlaces = async (stateCode) => {
    setLoadingPlaces(true);
    try {
      const { data } = await axios.get(`${API}/org/service-areas/regions/places`, { params: { state: stateCode }, withCredentials: true });
      setPlaces(data.places || []);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoadingPlaces(false);
    }
  };

  const togglePlace = (name) => {
    setSelectedPlaces((prev) => {
      const next = new Set(prev);
      next.has(name) ? next.delete(name) : next.add(name);
      return next;
    });
  };

  const addSelectedPlaces = () => {
    if (!selectedPlaces.size) return;
    const additions = Array.from(selectedPlaces).map((name) => ({ level: "city", state: selectedState, name }));
    const next = [...regions, ...additions];
    setRegions(next);
    setSelectedPlaces(new Set());
    runPreview(next);
  };

  const addZip = async () => {
    const zip5 = zipInput.trim();
    setZipError("");
    if (!/^\d{5}$/.test(zip5)) { setZipError("Enter a valid 5-digit ZIP code"); return; }
    if (regions.some((r) => r.level === "zip" && r.name === zip5)) { setZipError("That ZIP code is already added"); return; }
    setValidatingZip(true);
    try {
      const { data } = await axios.post(`${API}/org/service-areas/regions/validate-zip`, { zip: zip5, state: selectedState || null }, { withCredentials: true });
      const next = [...regions, { level: "zip", state: selectedState || null, name: data.zip }];
      setRegions(next);
      setZipInput("");
      runPreview(next);
    } catch (err) {
      setZipError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setValidatingZip(false);
    }
  };

  const startManualDraw = () => {
    setError("");
    pushHistory();
    drawnItemsRef.current?.clearLayers();
    if (circleRef.current) { mapRef.current.removeLayer(circleRef.current); circleRef.current = null; }
    new L.Draw.Polygon(mapRef.current, { shapeOptions: { color: "#0052FF" } }).enable();
    setManualOverride(true);
    setPreview(null);
  };

  const toggleEditPoints = () => {
    const map = mapRef.current;
    if (!map || !drawnItemsRef.current) return;
    if (!editingPoints) {
      pushHistory();
      editHandlerRef.current = new L.EditToolbar.Edit(map, { featureGroup: drawnItemsRef.current });
      editHandlerRef.current.enable();
      setEditingPoints(true);
    } else {
      editHandlerRef.current?.save();
      editHandlerRef.current?.disable();
      editHandlerRef.current = null;
      setManualOverride(true);
      setEditingPoints(false);
      checkOverlapDebounced();
    }
  };

  const clearBoundary = () => {
    pushHistory();
    drawnItemsRef.current?.clearLayers();
    if (circleRef.current) { mapRef.current.removeLayer(circleRef.current); circleRef.current = null; }
    setRegions([]);
    setInterpretation(null);
    setPreview(null);
    setManualOverride(true);
    setOverlaps([]);
  };

  const undo = () => {
    const prev = historyRef.current.pop();
    renderGeometry(prev || null);
  };

  const resetBoundary = () => {
    historyRef.current = [];
    setRegions(loadedGeo?.boundary_entities?.map((e) => ({ level: e.level, state: e.state, name: e.name })) || []);
    setInterpretation(loadedGeo?.llm_interpretation || null);
    setPreview(null);
    setManualOverride(!loadedGeo);
    renderGeometry(loadedGeo || null);
  };

  const runSearch = (text) => {
    setSearchQuery(text);
    clearTimeout(searchTimerRef.current);
    if (text.trim().length < 3) { setSearchResults([]); return; }
    searchTimerRef.current = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/geo/autocomplete`, { params: { text }, withCredentials: true });
        setSearchResults(data.suggestions || []);
      } catch { setSearchResults([]); }
    }, 350);
  };

  const flyToResult = (r) => {
    mapRef.current?.setView([r.lat, r.lng], 11);
    setSearchQuery(r.formatted || "");
    setSearchResults([]);
  };

  const handleSave = async () => {
    if (!name.trim()) { setError("Service area name is required."); return; }
    setSaving(true);
    setError("");
    try {
      let currentId = areaId;
      const serviceIdsPayload = allServices ? [] : Array.from(selectedServiceIds);
      if (!currentId) {
        const { data } = await axios.post(`${API}/org/service-areas`, { name: name.trim(), description: description.trim() || null }, { withCredentials: true });
        currentId = data.service_area.service_area_id;
        setAreaId(currentId);
        await axios.patch(`${API}/org/service-areas/${currentId}`, { status, service_ids: serviceIdsPayload }, { withCredentials: true });
      } else {
        await axios.patch(`${API}/org/service-areas/${currentId}`, { name: name.trim(), description: description.trim() || null, status, service_ids: serviceIdsPayload }, { withCredentials: true });
      }

      const geometry = captureSnapshot();
      if (geometry) {
        const isManualEdit = manualOverride || !preview;
        const body = { kind: geometry.kind, is_manual_edit: isManualEdit };
        if (geometry.kind === "radius") {
          body.center = geometry.center;
          body.radius_miles = geometry.radius_miles;
        } else {
          body.geometry = geometry.geometry;
        }
        if (!isManualEdit) {
          body.source_description = aiText.trim() || regions.map((r) => r.name).join(", ");
          body.llm_interpretation = interpretation;
          body.boundary_entities = preview.boundary_entities;
          body.excluded_entities = preview.excluded_entities;
          body.boundary_source_vintage = preview.boundary_source_vintage;
        }
        await axios.patch(`${API}/org/service-areas/${currentId}/geography`, body, { withCredentials: true });
      }
      onSaved();
      onClose();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const zoomIn = () => mapRef.current?.zoomIn();
  const zoomOut = () => mapRef.current?.zoomOut();

  const filteredCounties = counties.filter((c) => c.toLowerCase().includes(countyQuery.toLowerCase()));

  return (
    <div className="fixed inset-0 z-[1100] flex items-center justify-center bg-slate-950/60 p-4" data-testid="service-area-editor-overlay">
      <div className="flex h-[88vh] w-[90vw] max-w-[1700px] flex-col overflow-hidden rounded-2xl bg-white shadow-2xl dark:bg-[#0B1220]" data-testid="service-area-editor-modal">
        <div className="flex items-start justify-between gap-4 border-b border-slate-200 px-6 py-4 dark:border-white/10">
          <div className="grid flex-1 gap-3 sm:grid-cols-2">
            <div>
              <label className={labelClass}>Service Area Name *</label>
              <input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Northern New Jersey" data-testid="sa-editor-name-input" className={`mt-1 ${inputClass}`} />
            </div>
            <div>
              <label className={labelClass}>Territory Description (Optional)</label>
              <input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="e.g. Downtown coverage zone" data-testid="sa-editor-description-input" className={`mt-1 ${inputClass}`} />
            </div>
          </div>
          <div className="flex items-center gap-3 pt-6">
            <button type="button" onClick={() => setStatus(status === "Active" ? "Inactive" : "Active")} data-testid="sa-editor-status-toggle" className={`rounded-lg border px-3 py-2 text-xs font-bold ${status === "Active" ? "border-emerald-200 text-emerald-600 dark:border-emerald-500/30 dark:text-emerald-400" : "border-slate-200 text-slate-500 dark:border-white/15 dark:text-slate-400"}`}>
              {status}
            </button>
            <button type="button" onClick={onClose} data-testid="sa-editor-close-btn" className="rounded-full border border-slate-200 p-2 text-slate-500 hover:bg-slate-50 dark:border-white/10 dark:text-slate-400 dark:hover:bg-white/10">
              <X size={18} />
            </button>
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 px-6 py-3 dark:border-white/10">
          <div className="flex flex-wrap items-center gap-2">
            <button type="button" onClick={startManualDraw} data-testid="sa-editor-draw-btn" className={`${toolBtn} ${toolBtnIdle}`}><PenLine size={13} /> Draw Boundary</button>
            <button type="button" onClick={toggleEditPoints} data-testid="sa-editor-edit-points-btn" className={`${toolBtn} ${editingPoints ? toolBtnActive : toolBtnIdle}`}><Pencil size={13} /> {editingPoints ? "Finish Editing" : "Edit Points"}</button>
            <button type="button" onClick={undo} data-testid="sa-editor-undo-btn" className={`${toolBtn} ${toolBtnIdle}`}><Undo2 size={13} /> Undo</button>
            <button type="button" onClick={clearBoundary} data-testid="sa-editor-clear-btn" className={`${toolBtn} ${toolBtnIdle}`}><Eraser size={13} /> Clear Boundary</button>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search size={13} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input value={searchQuery} onChange={(e) => runSearch(e.target.value)} placeholder="Search for a location…" data-testid="sa-editor-search-input" className="w-56 rounded-lg border border-slate-300 py-2 pl-8 pr-3 text-xs dark:border-white/15 dark:bg-white/5 dark:text-white" />
              {searchResults.length > 0 && (
                <div className="absolute left-0 top-full z-10 mt-1 w-72 overflow-hidden rounded-lg border border-slate-200 bg-white shadow-lg dark:border-white/10 dark:bg-[#111C33]" data-testid="sa-editor-search-results">
                  {searchResults.slice(0, 6).map((r, i) => (
                    <button key={i} type="button" onClick={() => flyToResult(r)} className="block w-full px-3 py-2 text-left text-xs text-slate-600 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-white/10">{r.formatted}</button>
                  ))}
                </div>
              )}
            </div>
            <button type="button" onClick={zoomIn} data-testid="sa-editor-zoom-in-btn" className="rounded-lg border border-slate-200 px-2.5 py-2 text-xs font-bold text-slate-600 hover:bg-slate-50 dark:border-white/15 dark:text-slate-200 dark:hover:bg-white/10">+</button>
            <button type="button" onClick={zoomOut} data-testid="sa-editor-zoom-out-btn" className="rounded-lg border border-slate-200 px-2.5 py-2 text-xs font-bold text-slate-600 hover:bg-slate-50 dark:border-white/15 dark:text-slate-200 dark:hover:bg-white/10">−</button>
          </div>
        </div>

        <div className="flex flex-1 overflow-hidden">
          <div className="relative flex-1">
            {overlaps.length > 0 && (
              <div className="absolute left-3 right-3 top-3 z-[500] flex items-center gap-2 rounded-xl border border-amber-300 bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-800 shadow-lg dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300" data-testid="sa-editor-overlap-warning">
                <AlertTriangle size={14} className="shrink-0" />
                This boundary overlaps with: {overlaps.map((o) => o.name).join(", ")}. Overlaps are allowed — just confirm this is intended.
              </div>
            )}
            <div ref={mapElRef} data-testid="sa-editor-map" className="h-full w-full" />
          </div>

          <div className="w-[340px] shrink-0 overflow-y-auto border-l border-slate-200 p-4 dark:border-white/10" data-testid="sa-editor-sidebar">
            <div className="mb-3">
              <label className={labelClass}>Services Offered</label>
              <label className="mt-2 flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-200">
                <input type="checkbox" checked={allServices} onChange={(e) => setAllServices(e.target.checked)} data-testid="sa-editor-all-services-checkbox" />
                All Services
              </label>
              {!allServices && (
                <div className="mt-2 max-h-32 space-y-1 overflow-y-auto rounded-lg border border-slate-200 p-2 dark:border-white/10" data-testid="sa-editor-services-list">
                  {(availableServices || []).length === 0 && <p className="text-xs text-slate-400">No services created yet.</p>}
                  {(availableServices || []).map((s) => (
                    <label key={s.service_id} className="flex items-center gap-2 text-xs font-semibold text-slate-600 dark:text-slate-300">
                      <input
                        type="checkbox"
                        checked={selectedServiceIds.has(s.service_id)}
                        onChange={() => setSelectedServiceIds((prev) => { const next = new Set(prev); next.has(s.service_id) ? next.delete(s.service_id) : next.add(s.service_id); return next; })}
                        data-testid={`sa-editor-service-checkbox-${s.service_id}`}
                      />
                      {s.name}
                    </label>
                  ))}
                </div>
              )}
            </div>

            <div className="mb-3 flex rounded-lg border border-slate-200 p-1 dark:border-white/10">
              <button type="button" onClick={() => setRightTab("ai")} data-testid="sa-editor-tab-ai" className={`flex-1 rounded-md py-1.5 text-xs font-bold ${rightTab === "ai" ? "bg-[#0052FF] text-white" : "text-slate-500 dark:text-slate-400"}`}><Sparkles size={12} className="mr-1 inline" /> AI Assist</button>
              <button type="button" onClick={() => setRightTab("region")} data-testid="sa-editor-tab-region" className={`flex-1 rounded-md py-1.5 text-xs font-bold ${rightTab === "region" ? "bg-[#0052FF] text-white" : "text-slate-500 dark:text-slate-400"}`}><Layers size={12} className="mr-1 inline" /> State/County/City/ZIP</button>
            </div>

            {rightTab === "ai" ? (
              <div className="space-y-2">
                <label className={labelClass}>Describe the territory</label>
                <textarea value={aiText} onChange={(e) => setAiText(e.target.value)} rows={3} placeholder="e.g. NJ, NYC including Long Island, PA" data-testid="sa-editor-ai-text-input" className={inputClass} />
                <button type="button" onClick={interpretAndPreview} disabled={interpreting || previewing || !aiText.trim()} data-testid="sa-editor-interpret-btn" className="inline-flex w-full items-center justify-center gap-1.5 rounded-xl bg-[#0052FF] px-4 py-2 text-xs font-bold text-white disabled:opacity-50">
                  <Sparkles size={13} /> {interpreting || previewing ? "Interpreting…" : "Interpret & Preview"}
                </button>
                {interpretation && (
                  <div className="rounded-lg bg-slate-50 px-3 py-2 text-xs dark:bg-white/5" data-testid="sa-editor-interpretation">
                    <p className="text-slate-500 dark:text-slate-400">{interpretation.summary}</p>
                    {interpretation.is_informal_region && (
                      <div className="mt-1 flex items-center gap-1.5 text-amber-600 dark:text-amber-400">
                        <AlertTriangle size={11} /><span className="font-semibold">Informal region — review before confirming.</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                <div>
                  <label className={labelClass}>{isUS ? "State" : "State / Province"}</label>
                  <select value={selectedState} onChange={(e) => onStateChange(e.target.value)} data-testid="sa-editor-state-select" className={`mt-1 ${inputClass}`}>
                    <option value="">{isUS ? "Select a state…" : "Select a state / province…"}</option>
                    {stateOptions.map((s) => <option key={s.code} value={s.code}>{s.name}</option>)}
                  </select>
                  {selectedState && isUS && (
                    <button type="button" onClick={addWholeState} data-testid="sa-editor-add-whole-state-btn" className="mt-2 w-full rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-bold text-slate-600 hover:bg-slate-50 dark:border-white/15 dark:text-slate-200 dark:hover:bg-white/10">Add entire state</button>
                  )}
                </div>

                {!isUS && (
                  <p className="rounded-lg border border-dashed border-slate-200 p-2.5 text-xs text-slate-500 dark:border-white/15 dark:text-slate-400" data-testid="sa-editor-non-us-notice">
                    County/City search and ZIP validation are currently available for US addresses only. Use <strong>Draw Boundary</strong> on the map or <strong>AI Assist</strong> to define this territory.
                  </p>
                )}

                {selectedState && isUS && (
                  <div>
                    <label className={labelClass}>County</label>
                    <input value={countyQuery} onChange={(e) => setCountyQuery(e.target.value)} placeholder="Search counties…" data-testid="sa-editor-county-search" className={`mt-1 ${inputClass}`} />
                    <div className="mt-1 max-h-28 space-y-1 overflow-y-auto rounded-lg border border-slate-200 p-2 dark:border-white/10" data-testid="sa-editor-county-list">
                      {loadingCounties && <p className="text-xs text-slate-400">Loading counties…</p>}
                      {!loadingCounties && filteredCounties.map((c) => (
                        <label key={c} className="flex items-center gap-2 text-xs font-semibold text-slate-600 dark:text-slate-300">
                          <input type="checkbox" checked={selectedCounties.has(c)} onChange={() => toggleCounty(c)} data-testid={`sa-editor-county-checkbox-${c.replace(/\s+/g, "-")}`} />
                          {c}
                        </label>
                      ))}
                    </div>
                    <button type="button" onClick={addSelectedCounties} disabled={!selectedCounties.size} data-testid="sa-editor-add-counties-btn" className="mt-1 w-full rounded-lg bg-[#0052FF] px-3 py-1.5 text-xs font-bold text-white disabled:opacity-50">Add Selected Counties</button>
                  </div>
                )}

                {selectedState && isUS && (
                  <div>
                    <label className={labelClass}>City</label>
                    <input value={placeQuery} onChange={(e) => setPlaceQuery(e.target.value)} placeholder="Search cities…" data-testid="sa-editor-city-search" className={`mt-1 ${inputClass}`} />
                    <div className="mt-1 max-h-28 space-y-1 overflow-y-auto rounded-lg border border-slate-200 p-2 dark:border-white/10" data-testid="sa-editor-city-list">
                      {loadingPlaces && <p className="text-xs text-slate-400">Loading cities…</p>}
                      {!loadingPlaces && places.filter((p) => p.toLowerCase().includes(placeQuery.toLowerCase())).slice(0, 200).map((p) => (
                        <label key={p} className="flex items-center gap-2 text-xs font-semibold text-slate-600 dark:text-slate-300">
                          <input type="checkbox" checked={selectedPlaces.has(p)} onChange={() => togglePlace(p)} data-testid={`sa-editor-city-checkbox-${p.replace(/\s+/g, "-")}`} />
                          {p}
                        </label>
                      ))}
                    </div>
                    <button type="button" onClick={addSelectedPlaces} disabled={!selectedPlaces.size} data-testid="sa-editor-add-cities-btn" className="mt-1 w-full rounded-lg bg-[#0052FF] px-3 py-1.5 text-xs font-bold text-white disabled:opacity-50">Add Selected Cities</button>
                  </div>
                )}

                {isUS && (
                  <div>
                    <label className={labelClass}>ZIP Code</label>
                    <div className="mt-1 flex gap-1.5">
                      <input value={zipInput} onChange={(e) => setZipInput(e.target.value.replace(/\D/g, "").slice(0, 5))} placeholder="e.g. 07030" data-testid="sa-editor-zip-input" className={inputClass} />
                      <button type="button" onClick={addZip} disabled={validatingZip || zipInput.length !== 5} data-testid="sa-editor-add-zip-btn" className="shrink-0 rounded-lg bg-[#0052FF] px-3 py-2 text-xs font-bold text-white disabled:opacity-50">{validatingZip ? "Checking…" : "Add ZIP"}</button>
                    </div>
                    {zipError && <p className="mt-1 text-xs font-semibold text-red-600 dark:text-red-400" data-testid="sa-editor-zip-error">{zipError}</p>}
                  </div>
                )}
              </div>
            )}

            <div className="mt-4 border-t border-slate-100 pt-3 dark:border-white/10">
              <label className={labelClass}>Boundary Status &amp; Details</label>
              {loadedGeo?.confirmation_status && (
                <div className="mt-2 flex items-center gap-1.5 text-xs font-semibold text-slate-500 dark:text-slate-400" data-testid="sa-editor-boundary-status">
                  <ShieldCheck size={12} className="text-emerald-500" />
                  {loadedGeo.confirmation_status === "manually_adjusted" ? "Manually adjusted boundary" : "AI-assisted boundary confirmed"}
                </div>
              )}
              {regions.length === 0 ? (
                <p className="mt-2 text-xs text-slate-400" data-testid="sa-editor-no-regions">No named regions selected. Draw a boundary manually or add one above.</p>
              ) : (
                <div className="mt-2 flex flex-wrap gap-1.5" data-testid="sa-editor-region-chips">
                  {regions.map((r, i) => (
                    <span key={`${r.level}-${r.name}-${i}`} className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2 py-1 text-[11px] font-semibold text-blue-700 dark:bg-blue-500/10 dark:text-blue-400">
                      {r.name}{r.state ? `, ${r.state}` : ""}
                      <button type="button" onClick={() => removeRegion(i)} data-testid={`sa-editor-remove-region-${i}`} className="text-blue-400 hover:text-red-500"><X size={10} /></button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between gap-4 border-t border-slate-200 px-6 py-4 dark:border-white/10">
          <div className="flex-1">
            {error && <p className="text-xs font-semibold text-red-600 dark:text-red-400" data-testid="sa-editor-error">{error}</p>}
          </div>
          <div className="flex items-center gap-2">
            <button type="button" onClick={onClose} data-testid="sa-editor-cancel-btn" className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-bold text-slate-600 hover:bg-slate-50 dark:border-white/15 dark:text-slate-200 dark:hover:bg-white/10">Cancel</button>
            <button type="button" onClick={resetBoundary} data-testid="sa-editor-reset-btn" className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-bold text-slate-600 hover:bg-slate-50 dark:border-white/15 dark:text-slate-200 dark:hover:bg-white/10"><RotateCcw size={14} /> Reset Boundary</button>
            <button type="button" onClick={handleSave} disabled={saving} data-testid="sa-editor-save-btn" className="inline-flex items-center gap-1.5 rounded-xl bg-emerald-600 px-5 py-2.5 text-sm font-bold text-white disabled:opacity-50"><Check size={15} /> {saving ? "Saving…" : "Save Service Area"}</button>
          </div>
        </div>
      </div>
    </div>
  );
};
